import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateCustomerPdfStatRequest,
  CreateCustomerPdfStatRequestSchema,
  type CustomerPdfStat,
  type UpdateCustomerPdfStatRequest,
  UpdateCustomerPdfStatRequestSchema,
} from '@utazzo/shared';
import { UzButton, UzDrawer, UzForm, UzFormField, UzInput, UzNumberInput } from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateStat, useUpdateStat } from '../use-customer-pdf';

import { MediaDropField } from '@/features/website-cms/components/MediaDropField';

interface StatFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  stat?: CustomerPdfStat | null;
}

/**
 * Create/edit drawer for a stats strip tile at the bottom of the customer
 * page: icon, bold value ("100K+ Happy"), label ("travellers") and an
 * optional 0-5 star rating (e.g. 4.4 for the Google tile).
 */
export const StatFormDrawer = ({
  open,
  onOpenChange,
  mode,
  stat,
}: StatFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createMut = useCreateStat();
  const updateMut = useUpdateStat();
  const pending = createMut.isPending || updateMut.isPending;

  const createForm = useForm<CreateCustomerPdfStatRequest>({
    resolver: zodResolver(CreateCustomerPdfStatRequestSchema),
    defaultValues: { value: '', label: '', iconImageId: null, rating: null, sortOrder: 0 },
  });
  const updateForm = useForm<UpdateCustomerPdfStatRequest>({
    resolver: zodResolver(UpdateCustomerPdfStatRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset({ value: '', label: '', iconImageId: null, rating: null, sortOrder: 0 });
    } else if (stat) {
      updateForm.reset({
        value: stat.value,
        label: stat.label,
        iconImageId: stat.iconImageId,
        rating: stat.rating,
        sortOrder: stat.sortOrder,
      });
    }
  }, [open, isCreate, stat, createForm, updateForm]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = (isCreate ? createForm : updateForm) as ReturnType<typeof useForm<any>>;

  const onCreate = createForm.handleSubmit(async (values) => {
    try {
      await createMut.mutateAsync(values);
      onOpenChange(false);
    } catch {
      /* toast already fired in mutation hook */
    }
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!stat) return;
    try {
      await updateMut.mutateAsync({ id: stat.id, payload: values });
      onOpenChange(false);
    } catch {
      /* toast already fired */
    }
  });

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add stat' : 'Edit stat'}
      description="Shown in the stats strip at the bottom of the Awards & Partners section."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Add stat' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      <UzForm form={form} onSubmit={isCreate ? onCreate : onUpdate}>
        <UzFormField name="iconImageId">
          {({ field }) => (
            <MediaDropField
              value={field.value ?? null}
              onChange={(id) => field.onChange(id)}
              label="Icon"
              folder="customer-pdf"
              variant="square"
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="value">
          {({ field, error }) => (
            <UzInput
              {...field}
              label="Value"
              placeholder="100K+ Happy"
              hint="The bold headline, e.g. '100K+ Happy', '15+ Years', '4.4 Rated'."
              required
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        <UzFormField name="label">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={field.value ?? ''}
              label="Label"
              placeholder="travellers"
              hint="The smaller second line, e.g. 'travellers' / 'of experience'. Optional."
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        <UzFormField name="rating">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              label="Star rating (optional)"
              value={field.value ?? undefined}
              onValueChange={(v) => field.onChange(v ?? null)}
              min={0}
              max={5}
              step={0.1}
              hint="Renders gold stars under the value, e.g. 4.4. Leave empty for no stars."
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="sortOrder">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              label="Sort order"
              value={field.value ?? 0}
              onValueChange={(v) => field.onChange(v ?? 0)}
              min={0}
              max={9999}
              step={10}
              hint="Lower values appear first."
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </UzForm>
    </UzDrawer>
  );
};
